import { PauseIcon, PlayIcon, Volume2, VolumeOff, X } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import PlayerSideControl from "./PlayerSideControl";
import type { VideoVerticalPlayerSideControls } from "./PlayerSideControl";
import { cn } from "@/lib/utils";
import { useAppStore } from "@/stores/useApp";
import ExclusiveBadge from "@/components/ExclusiveBadge";

interface PlayerControlsProps extends React.HTMLAttributes<HTMLElement> {
  isActive: boolean;
  isPlaying: boolean;
  sideControls?: Array<VideoVerticalPlayerSideControls>;
  onPlayPause: () => void;
  onMuteChange?: (muted: boolean) => void;
  onClose?: () => void;
}

type PlayerControls = React.FC<PlayerControlsProps> & {
  Skeleton: React.FC<{
    className?: string;
    children?: React.ReactNode;
    sideControls: Array<VideoVerticalPlayerSideControls>;
  }>;
};

const Skeleton: React.FC<{
  className?: string;
  children?: React.ReactNode;
  sideControls: Array<VideoVerticalPlayerSideControls>;
}> = ({ className, sideControls, children }) => (
  <div
    className={cn(
      "absolute top-0 right-0 bottom-0 left-0 z-10 flex items-center justify-between overflow-hidden",
      className,
    )}
  >
    <div className="absolute top-8 right-2 bottom-8 z-10 flex w-16 flex-col justify-between gap-4">
      <PlayerSideControl.Skeleton icon={Volume2} />
      {!!sideControls.length && (
        <div className="flex flex-col gap-4">
          {sideControls.map(({ icon }, index) => (
            <PlayerSideControl.Skeleton key={index} icon={icon} label="any" />
          ))}
        </div>
      )}
    </div>

    {children && (
      <div className="absolute right-0 bottom-0 left-0 min-h-10 max-w-96 bg-linear-to-t to-transparent pr-16">
        {children}
      </div>
    )}
  </div>
);

const PlayPauseButton: React.FC<{
  isPlaying: boolean;
  shouldShowControls: boolean;
  onClick: () => void;
}> = ({ isPlaying, shouldShowControls, onClick }) => (
  <button
    className={cn(
      "pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 transition-opacity",
      shouldShowControls && "pointer-events-auto opacity-100 duration-300",
    )}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
  >
    {isPlaying ? (
      <PauseIcon className="size-12" fill="currentColor" />
    ) : (
      <PlayIcon className="size-12" fill="currentColor" />
    )}
  </button>
);

const PlayerControls: PlayerControls = ({
  isActive: _isActive,
  isPlaying,
  className,
  sideControls,
  children,
  onPlayPause,
  onMuteChange,
  onClose,
  ...props
}) => {
  const controlsDisplayTimeout = useRef<NodeJS.Timeout | null>(null);
  const soundMuted = useAppStore((s) => s.soundMuted);

  const [showControls, setShowControls] = useState(false);
  const [isUserActive, setIsUserActive] = useState(false);

  const shouldShowControls = showControls || !isPlaying;

  const onBackgroundClick = () => {
    setShowControls(!showControls);
    setIsUserActive(false);
  };

  const toggleSound = useCallback(() => {
    const muted = !soundMuted;
    useAppStore.setState({ soundMuted: muted });
    onMuteChange?.(muted);
  }, [soundMuted, onMuteChange]);

  useEffect(() => {
    if (showControls && !isUserActive && isPlaying)
      controlsDisplayTimeout.current = setTimeout(() => {
        setShowControls(false);
      }, 5000);
    else if (controlsDisplayTimeout.current)
      clearTimeout(controlsDisplayTimeout.current);

    return () => {
      if (controlsDisplayTimeout.current)
        clearTimeout(controlsDisplayTimeout.current);
    };
  }, [showControls, isUserActive, isPlaying]);

  return (
    <div
      className={cn(
        "absolute top-0 right-0 bottom-0 left-0 z-30 flex items-center justify-between overflow-hidden bg-transparent transition-[background-color_0.3s_ease-in-out]",
        shouldShowControls && "bg-black/30",
        className,
      )}
      {...props}
      onClick={onBackgroundClick}
      onMouseEnter={() => setIsUserActive(true)}
      onMouseLeave={() => setIsUserActive(false)}
    >
      <PlayPauseButton
        isPlaying={isPlaying}
        shouldShowControls={shouldShowControls}
        onClick={onPlayPause}
      />

      <div
        className={cn(
          "from-neutral-70/50 pointer-events-none absolute top-0 right-0 left-0 z-20 flex h-16 items-start justify-between gap-4 bg-linear-to-b to-transparent px-2 pt-8",
          shouldShowControls && "pointer-events-auto",
        )}
      >
        <div>
          {onClose && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className={cn(
                "pointer-events-none size-16 w-16 opacity-0 transition-opacity",
                shouldShowControls &&
                  "pointer-events-auto opacity-100 duration-300",
              )}
            >
              <X className="mx-auto size-8" />
            </button>
          )}
        </div>
        <div className="flex flex-col items-center gap-2">
          <ExclusiveBadge className="size-16 p-2 text-white opacity-30" />
          <PlayerSideControl
            testId="player-mute"
            isDisplayed={shouldShowControls}
            icon={soundMuted ? VolumeOff : Volume2}
            onClick={(e) => {
              e.stopPropagation();
              toggleSound();
            }}
          />
        </div>
      </div>

      <div
        className={cn(
          "pointer-events-none absolute top-8 right-2 bottom-8 z-10 flex w-16 flex-col justify-end gap-4 lg:hidden",
          shouldShowControls && "pointer-events-auto",
        )}
      >
        {!!sideControls?.length && (
          <div className="flex flex-col gap-4">
            {sideControls.map((control, index) => (
              <PlayerSideControl
                key={index}
                sideControlsLength={sideControls.length}
                {...control}
                index={index}
                isDisplayed={shouldShowControls}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsUserActive(true);
                  control.onClick?.(e);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {children && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            setIsUserActive(true);
          }}
          className={cn(
            "from-neutral-70 pointer-events-none absolute right-0 bottom-0 left-0 min-h-10 bg-linear-to-t to-transparent pr-16 opacity-0 transition-opacity duration-300",
            shouldShowControls && "pointer-events-auto opacity-100",
          )}
        >
          <div className="max-w-96">{children}</div>
        </div>
      )}
    </div>
  );
};

PlayerControls.Skeleton = Skeleton;

export default PlayerControls;
